import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ArrowLeft, ArrowRight, ShieldCheck, 
  Receipt, Building2, FileCheck, Users, Trees, Flame, 
  Cpu, MapPin, Calendar, Calculator, Building, Landmark,
  Scale, FileText, CheckSquare, AlertTriangle, Coins
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface TaxCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const TAX_AGENT_IDS = [
  'tax-pn-unblocker-auditor',
  'tax-transfer-pricing-doc-architect',
  'tax-ksef-vat-auditor',
  'it-fatturapa-sdi-auditor',
  'es-facturae-verifactu-auditor',
  'pt-saft-fatura-auditor',
  'se-peppol-svefaktura-auditor',
  'de-xrechnung-zugferd-auditor',
  'fr-facturx-chorus-auditor',
  'fop-tax-declarator',
  'corporate-income-tax-declarator',
  'vat-tax-declarator',
  'payroll-tax-declarator',
  'resource-property-tax-declarator',
  'excise-tax-declarator',
  'special-tax-regimes-declarator',
  'local-fees-declarator',
  'tax-audit-calendar-agent',
  'tax-financial-analyst',
  'public-accountant-kekv',
  'agri-mpz-calculator',
  'cfo-financial-analyzer',
  'budget-program-passport',
  'budget-koshtorys-builder',
  'npsbods-accounting-auditor',
  'budget-payroll-auditor',
  'dostupni-liky-reimbursement',
  'legislation-tracker',
  'windykacja-epu-odsetki-generator',
  'diia-city-compliance-gig-architect'
];

// Helper icon renderer
const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Landmark className={className} />;
  return <IconComponent className={className} />;
};

export default function TaxCenterHubView({
  onLaunchAgent,
  onBack,
  language, currency = 'UAH'}: TaxCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [selectedSubTab, setSelectedSubTab] = useState<'all' | 'simplified' | 'cit_vat' | 'payroll' | 'property_eco'>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch tax pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const allTaxAgents = agentsConfig.filter((a: any) => TAX_AGENT_IDS.includes(a.id));

  const filteredAgents = allTaxAgents.filter((agent: any) => {
    if (selectedSubTab === 'all') return true;
    if (selectedSubTab === 'simplified') {
      return ['fop-tax-declarator', 'special-tax-regimes-declarator'].includes(agent.id);
    }
    if (selectedSubTab === 'cit_vat') {
      return ['corporate-income-tax-declarator', 'vat-tax-declarator', 'tax-financial-analyst'].includes(agent.id);
    }
    if (selectedSubTab === 'payroll') {
      return ['payroll-tax-declarator', 'public-accountant-kekv'].includes(agent.id);
    }
    if (selectedSubTab === 'property_eco') {
      return ['resource-property-tax-declarator', 'excise-tax-declarator', 'local-fees-declarator', 'tax-audit-calendar-agent'].includes(agent.id);
    }
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            {isPl ? '🏛️ 19 Modułów Podatkowych & Księgowych' : isUk ? '🏛️ 19 Податкових Агентів ПКУ' : '🏛️ 19 Tax & Accounting Agents'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-blue-400" />
            {isPl ? '100% Zgodność z KSeF, VAT & CIT' : isUk ? '100% ПКУ & ДПС Сумісність' : '100% Tax & Compliance'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
              {isPl ? 'PRAWO PODATKOWE • KSEF • JPK_V7 • DEKLARACJE CIT / PIT • ROZLICZENIA ZUS' : isUk ? 'ПОДАТКОВИЙ КОДЕКС УКРАЇНИ • ЗВІТНІСТЬ ДПС • РОЗРАХУНОК ПОДАТКІВ & ДЕКЛАРАЦІЙ' : 'TAX CODE • TAX DECLARATIONS • TAX AUDIT & COMPLIANCE'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-slate-100 to-emerald-300 bg-clip-text text-transparent">
            {isPl 
              ? 'CENTRUM PODATKOWE, KSEF & DEKLARACJE' 
              : isUk 
              ? 'ПОДАТКОВИЙ ЦЕНТР & ДЕКЛАРАЦІЇ ДПС' 
              : 'TAX COMMAND CENTER & DPS DECLARATIONS'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Specjalistyczny hub modułów analitycznych do obliczania zobowiązań podatkowych, audytu deklaracji i weryfikacji KSeF, JPK_V7, PIT, CIT Estońskiego, VAT, ZUS oraz podatków majątkowych.'
              : isUk
              ? 'Спеціалізований розрахунково-аналітичний хаб Аналітичних Модулів для обчислення податкових зобов\'язань, формування та камерального аудиту декларацій з усіх податків та зборів ПКУ (ФОП, ЄП 1-4, Прибуток, ПДВ, ПДФО/ЄСВ/ВЗ, Акциз, Земля, Нерухомість, Екологія, Дія Сіті).'
              : 'Specialized AI tax engine for computing liabilities, assembling, and auditing official Tax Declarations for all Tax Code duties (FOP, CIT, VAT, Payroll PIT/USC/Military, Excise, Land & Real Estate, Diia City).'}
          </p>
        </div>
      </div>

      {/* Flagship Anti-Blocking Tax Invoices Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border-2 border-emerald-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
        <div className="space-y-2 z-10 max-w-2xl">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-0.5 text-[9px] font-black uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>🔥 ГОЛОВНИЙ АНТИБЛОКУВАЛЬНИЙ МОДУЛЬ</span>
            </span>
            <span className="px-2.5 py-0.5 text-[9px] font-black uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded">
              ПКМУ № 1165 • СМКОР • ФОРМИ J/F1312603 & J/F1312303
            </span>
          </div>
          <h3 className="text-base sm:text-lg font-black uppercase tracking-wide text-emerald-200">
            🛡️ Розблокування Податкових Накладних (ПН/РК) та Зняття Ризиковості
          </h3>
          <p className="text-xs text-slate-300 leading-relaxed font-sans">
            Автоматизований аналіз квитанцій про зупинення, експрес-діагностика критеріїв ризиковості (п. 1 та п. 8), генерація мотивованих Письмових Пояснень, Таблиці даних платника та Скарги до ДПС України у Word (.docx за ДСТУ 4163:2020).
          </p>
        </div>

        <button
          onClick={() => onLaunchAgent('tax-pn-unblocker-auditor')}
          className="px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02] shrink-0"
        >
          <span>🚀 Відкрити Антиблокувальник ПН</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Sub-Category Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        <button
          onClick={() => setSelectedSubTab('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'all'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? 'Wszyscy Agenci Podatkowi (19)' : isUk ? 'Всі Податкові Агенти (19)' : 'All Tax Agents (19)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('simplified')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'simplified'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '💼 Ryczałt & JDG (2)' : isUk ? '💼 Спрощенка & ФОП (2)' : '💼 FOP & Simplified (2)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('cit_vat')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'cit_vat'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🏢 CIT & VAT / KSeF (3)' : isUk ? '🏢 Прибуток & ПДВ (3)' : '🏢 CIT & VAT (3)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('payroll')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'payroll'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '👥 Podatki Płacowe & ZUS (2)' : isUk ? '👥 Зарплатні Податки (2)' : '👥 Payroll Taxes (2)'}
        </button>

        <button
          onClick={() => setSelectedSubTab('property_eco')}
          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'property_eco'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? '🌿 Podatki Majątkowe & Akcyza (4)' : isUk ? '🌿 Майнові, Акциз & Календар (4)' : '🌿 Property, Excise & Audit (4)'}
        </button>
      </div>

      {/* Grid of Agent Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredAgents.map((agent: any) => {
          const agentPricing = pricing[agent.id];
          const priceVal = agentPricing ? agentPricing.price : 490;
          const limitVal = agentPricing ? agentPricing.limit : 30;
          const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
          const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;

          return (
            <div
              key={agent.id}
              className="group relative bg-white border border-slate-200 hover:border-emerald-500/40 rounded-xl p-6 flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200/60 group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {getLocalizedAgentCategory(agent, language)}
                      </span>
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                        ✨ Gemini 3.7 Flash
                      </span>
                      <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200 font-mono">
                        {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                      </span>
                      {singlePriceVal > 0 && (
                        <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                          {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mt-1.5 leading-snug">
                      {getLocalizedAgentName(agent, language)}
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-slate-600 leading-relaxed font-mono">
                  {getLocalizedAgentDesc(agent, language)}
                </p>

                {/* Features list */}
                <ul className="space-y-1.5 pt-1">
                  {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2 text-[11px] text-slate-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span className="truncate">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Launch Action */}
              <div className="pt-6 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200">
                  <Sparkles className="w-3 h-3 text-emerald-600 animate-pulse" />
                  <span>{isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}</span>
                </div>

                <button
                  onClick={() => onLaunchAgent(agent.target)}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md shadow-emerald-600/20 flex items-center gap-2 cursor-pointer hover:scale-[1.02]"
                >
                  <span>{isPl ? '🚀 Uruchom Zadanie' : isUk ? '🚀 Виконати Задачу' : '🚀 Execute Task'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

